import React, { useState, useEffect } from 'react'
import {
  number,
  func,
  bool,
  string,
} from 'prop-types'
import {
  Button,
  TextInput,
  ArrowLeft,
  ArrowRight,
  Icon,
  ArrowToLeft,
  ArrowToRight,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import { useCrawlingTags } from '../../useCrawlingTags'

function PaginationCard({
  setSelectedFilters,
  currentPage,
  totalPages,
  shouldSwapCrawlingTags,
  keywords = '',
  category = '',
}) {
  const [inputValue, setInputValue] = useState(Number(currentPage))

  const {
    hasCanonicalTag,
    hasRobotsTag,
    addCanonicalTag,
    removeCanonicalTag,
    addRobotsTag,
    removeRobotsTag,
    adjustCanonicalTag,
  } = useCrawlingTags(shouldSwapCrawlingTags)

  useEffect(() => {
    setInputValue(Number(currentPage))
  }, [currentPage])

  const isGoBackDisabled = Number(currentPage) === 1
  const isGoNextDisabled = totalPages === Number(currentPage)

  const getCanonicalHref = () => {
    const canonicalHref = window.location.toString().replace(window.location.search, '')
    return canonicalHref
  }

  const trackPagination = ({ page, source = 'manual input' }) => {
    window.customEventTracker({
      name: 'proceed_to_page',
      customAttributes: {
        event_source: source,
      },
      eventData: {
        page_number: `${page}`,
        search_term: keywords || category || '',
      },
    })
  }

  const handleGoNextPage = () => {
    const targetPage = Number(currentPage) + 1

    if (shouldSwapCrawlingTags) {
      if (hasCanonicalTag) {
        removeCanonicalTag()
      }

      if (!hasRobotsTag) {
        addRobotsTag()
      }
    } else {
      adjustCanonicalTag(`${getCanonicalHref()}?page=${targetPage}`)
    }

    trackPagination({ page: targetPage, source: 'next' })
    setSelectedFilters({ name: 'page', value: targetPage })
  }

  const handleGoPreviousPage = () => {
    const targetPage = Number(currentPage) - 1

    if (shouldSwapCrawlingTags) {
      if (targetPage === 1) {
        addCanonicalTag(getCanonicalHref())
        removeRobotsTag()
      }
    } else {
      adjustCanonicalTag(`${getCanonicalHref()}?page=${targetPage}`)
    }

    trackPagination({ page: targetPage, source: 'previous' })
    setSelectedFilters({ name: 'page', value: targetPage })
  }

  const handleGoToFirstPage = () => {
    if (shouldSwapCrawlingTags) {
      if (!hasCanonicalTag) {
        addCanonicalTag(getCanonicalHref())
      }

      if (hasRobotsTag) {
        removeRobotsTag()
      }
    } else {
      adjustCanonicalTag(`${getCanonicalHref()}?page=1`)
    }

    trackPagination({ page: 1 })
    setSelectedFilters({ name: 'page', value: 1 })
  }

  const handleGoToLastPage = () => {
    if (shouldSwapCrawlingTags) {
      if (hasCanonicalTag) {
        removeCanonicalTag()
      }

      if (!hasRobotsTag) {
        addRobotsTag()
      }
    } else {
      adjustCanonicalTag(`${getCanonicalHref()}?page=${totalPages}`)
    }

    trackPagination({ page: totalPages })
    setSelectedFilters({ name: 'page', value: totalPages })
  }

  const handleJumpToPage = (event) => {
    let isNumber = Number(inputValue)

    if (isNumber > totalPages) {
      setInputValue(totalPages)

      isNumber = totalPages
    }

    if (event.key === 'Enter') {
      if (shouldSwapCrawlingTags) {
        if (isNumber && isNumber > 1) {
          if (hasCanonicalTag) {
            removeCanonicalTag()
          }

          if (!hasRobotsTag) {
            addRobotsTag()
          }
        }

        if (isNumber && isNumber === 1) {
          if (!hasCanonicalTag) {
            addCanonicalTag(getCanonicalHref())
          }

          if (hasRobotsTag) {
            removeRobotsTag()
          }
        }
      } else {
        adjustCanonicalTag(`${getCanonicalHref()}?page=${isNumber}`)
      }

      trackPagination({ page: isNumber })
      return setSelectedFilters({ name: 'page', value: isNumber })
    }
  }

  const handleChange = (event) => {
    setInputValue(event.target.value)
  }

  return (
    <div className="metaverse-pagination-new__pagination-card">
      <Button
        type="secondary"
        disabled={isGoBackDisabled}
        onClick={handleGoToFirstPage}
        customClass="metaverse-pagination-new__first-page-button"
      >
        1
      </Button>
      <Button
        title="Go to first page"
        icon={<Icon svgComponent={ArrowToLeft} />}
        type="secondary"
        variant="only-icon"
        disabled={isGoBackDisabled}
        onClick={handleGoToFirstPage}
        customClass="metaverse-pagination-new__first-page-button--mobile"
      />
      <Button
        title="Go to previous page"
        icon={<Icon svgComponent={ArrowLeft} />}
        type="primary"
        variant="only-icon"
        disabled={isGoBackDisabled}
        onClick={handleGoPreviousPage}
        customClass="metaverse-pagination-new__arrow-button"
      />
      <TextInput
        id="input"
        inputType="text"
        aria-label="Jump to page"
        isPilot
        value={inputValue}
        onChange={handleChange}
        size="medium"
        type="primary"
        onKeyPress={handleJumpToPage}
        wrapperClassName="metaverse-pagination-new__jump-to-page-wrapper"
        classNames="metaverse-pagination-new__jump-to-page-field"
        className="metaverse-pagination-new__jump-to-page-input"
      />
      <Button
        title="Go to next page"
        icon={<Icon svgComponent={ArrowRight} />}
        type="primary"
        variant="only-icon"
        disabled={isGoNextDisabled}
        onClick={handleGoNextPage}
        customClass="metaverse-pagination-new__arrow-button"
      />
      <Button
        title="Go to last page"
        icon={<Icon svgComponent={ArrowToRight} />}
        type="secondary"
        variant="only-icon"
        disabled={isGoNextDisabled}
        onClick={handleGoToLastPage}
        customClass="metaverse-pagination-new__last-page-button--mobile"
      />
      <Button
        type="secondary"
        disabled={isGoNextDisabled}
        onClick={handleGoToLastPage}
        customClass="metaverse-pagination-new__last-page-button"
      >
        {totalPages}
      </Button>
    </div>
  )
}

PaginationCard.propTypes = {
  setSelectedFilters: func.isRequired,
  currentPage: number.isRequired,
  totalPages: number.isRequired,
  shouldSwapCrawlingTags: bool.isRequired,
  keywords: string,
  category: string,
}

const mapStateToProps = ({
  setSelectedFilters,
  currentMeta: { currentPage, totalPages },
  shouldSwapCrawlingTags,
  selectedFilters: { keywords },
  pageCategory,
}) => {
  return {
    setSelectedFilters,
    currentPage,
    totalPages,
    shouldSwapCrawlingTags,
    keywords,
    category: pageCategory,
  }
}

export default WithTriangleUiContext(mapStateToProps)(PaginationCard)
